"use client";

import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight, Globe, User } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { blogPageHref, POSTS_PER_PAGE } from "./pagination";

export type PostMeta = {
  slug: string;
  title: string;
  date: string;
  excerpt?: string;
  author?: string;
  thumbnail?: string;
  tags?: string[];
  language: "en" | "ml";
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  ml: "മലയാളം",
};

const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  "Product Updates": {
    bg: "bg-violet-500/20",
    text: "text-violet-700 dark:text-violet-400",
  },
  Announcements: {
    bg: "bg-sky-500/20",
    text: "text-sky-700 dark:text-sky-400",
  },
  "Fee Collection": {
    bg: "bg-emerald-500/20",
    text: "text-emerald-700 dark:text-emerald-400",
  },
  Tips: { bg: "bg-amber-500/20", text: "text-amber-700 dark:text-amber-400" },
  "Business Growth": {
    bg: "bg-teal-500/20",
    text: "text-teal-700 dark:text-teal-400",
  },
  Tutorials: {
    bg: "bg-rose-500/20",
    text: "text-rose-700 dark:text-rose-400",
  },
  WhatsApp: {
    bg: "bg-green-500/20",
    text: "text-green-700 dark:text-green-400",
  },
  "Getting Started": {
    bg: "bg-sky-500/20",
    text: "text-sky-700 dark:text-sky-400",
  },
  "Tips & Tricks": {
    bg: "bg-amber-500/20",
    text: "text-amber-700 dark:text-amber-400",
  },
  default: { bg: "bg-gray-500/20", text: "text-gray-700 dark:text-gray-400" },
};

function getTagColor(tag: string) {
  return TAG_COLORS[tag] || TAG_COLORS.default;
}

type BlogListClientProps = {
  allPosts: readonly PostMeta[];
  currentPage: number;
  showAllLanguages: boolean;
};

export function BlogListClient({ allPosts, currentPage, showAllLanguages }: BlogListClientProps) {
  const { language, t } = useTranslation();

  const filteredPosts = showAllLanguages
    ? allPosts
    : allPosts.filter((p) => p.language === language);

  const totalPosts = filteredPosts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  // The URL param is clamped server-side against the full post count; the
  // language filter runs client-side, so re-clamp against the filtered total.
  const safePage = Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1));
  const startIndex = (safePage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

  const otherLanguageCount = allPosts.filter((p) => p.language !== language).length;

  const content = t.blog;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <header className="mb-8 text-center">
            <h1 className={cn("text-4xl md:text-5xl font-bold text-foreground mb-4")}>
              {content.title}
            </h1>
            <p className={cn("text-lg text-muted-foreground")}>{content.subtitle}</p>
          </header>

          {otherLanguageCount > 0 && !showAllLanguages && (
            <div className="flex items-center justify-center mb-8">
              <Link
                href={blogPageHref(1, true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-dashed border-border rounded-lg hover:border-foreground/30 transition-colors"
              >
                <Globe className="h-4 w-4" />
                <span>
                  +{otherLanguageCount} {content.otherPosts}
                </span>
              </Link>
            </div>
          )}

          {showAllLanguages && (
            <div className="flex items-center justify-center mb-8">
              <Link
                href={blogPageHref(1, false)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Globe className="h-4 w-4" />
                <span>
                  {content.showingOnly} {LANGUAGE_LABELS[language]} {content.postsIn}
                </span>
                <span className="text-muted-foreground">|</span>
                <span>Showing all languages</span>
              </Link>
            </div>
          )}

          {filteredPosts.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg">
              <p className={cn("text-muted-foreground")}>{content.noPosts}</p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {paginatedPosts.map((post) => (
                  <article
                    key={post.slug}
                    className="group border border-border rounded-lg overflow-hidden hover:border-foreground/20 transition-colors flex flex-col bg-card/50 backdrop-blur-sm"
                  >
                    <Link href={`/blog/${post.slug}`} className="flex flex-col h-full">
                      {post.thumbnail && (
                        <div className="relative overflow-hidden bg-muted aspect-[2/1]">
                          <Image
                            src={post.thumbnail || "/placeholder.svg"}
                            alt={post.title}
                            fill
                            className="object-cover transition-transform duration-300 motion-safe:group-hover:scale-105"
                            // Frontmatter thumbnails may live on any host; skip the optimiser allowlist.
                            unoptimized
                          />
                        </div>
                      )}
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              post.language === "ml"
                                ? "bg-orange-500/20 text-orange-700 dark:text-orange-400"
                                : "bg-sky-500/20 text-sky-700 dark:text-sky-400"
                            }`}
                          >
                            {LANGUAGE_LABELS[post.language]}
                          </span>
                          {post.tags?.map((tag) => {
                            const colors = getTagColor(tag);
                            return (
                              <span
                                key={tag}
                                className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.bg} ${colors.text}`}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                        <h2
                          className={cn(
                            "text-lg font-semibold text-foreground mb-2 group-hover:underline line-clamp-2"
                          )}
                        >
                          {post.title}
                        </h2>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(post.date).toLocaleDateString(
                              post.language === "ml" ? "ml-IN" : "en-IN",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </span>
                          {post.author && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {post.author}
                            </span>
                          )}
                        </div>
                        {post.excerpt && (
                          <p className={cn("text-muted-foreground text-sm line-clamp-2 mb-3")}>
                            {post.excerpt}
                          </p>
                        )}
                        <span
                          className={cn(
                            "mt-auto inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all"
                          )}
                        >
                          {post.language === "ml" ? "കൂടുതൽ വായിക്കുക" : "Read more"}{" "}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {totalPages > 1 && (
                <nav
                  className="mt-12 flex items-center justify-center gap-2"
                  aria-label="Blog pagination"
                >
                  {safePage === 1 ? (
                    <span
                      aria-disabled="true"
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium border border-border rounded-lg text-muted-foreground cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {content.previous}
                    </span>
                  ) : (
                    <Link
                      href={blogPageHref(safePage - 1, showAllLanguages)}
                      aria-label="Go to previous page"
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {content.previous}
                    </Link>
                  )}

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <Link
                        key={pageNum}
                        href={blogPageHref(pageNum, showAllLanguages)}
                        aria-label={`Go to page ${pageNum}`}
                        aria-current={pageNum === safePage ? "page" : undefined}
                        className={`w-10 h-10 flex items-center justify-center text-sm font-medium rounded-lg transition-colors ${
                          pageNum === safePage
                            ? "bg-foreground text-background"
                            : "border border-border hover:bg-muted"
                        }`}
                      >
                        {pageNum}
                      </Link>
                    ))}
                  </div>

                  {safePage === totalPages ? (
                    <span
                      aria-disabled="true"
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium border border-border rounded-lg text-muted-foreground cursor-not-allowed"
                    >
                      {content.next}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  ) : (
                    <Link
                      href={blogPageHref(safePage + 1, showAllLanguages)}
                      aria-label="Go to next page"
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      {content.next}
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </nav>
              )}

              <p className={cn("mt-4 text-center text-sm text-muted-foreground")}>
                {content.showing} {startIndex + 1}-{Math.min(endIndex, totalPosts)} {content.of}{" "}
                {totalPosts} {content.posts}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
